import React, { useState } from 'react';
import { CompetitorBadges } from './CompetitorBadge';

// Bodies longer than this collapse behind a toggle. Mean body is ~220 chars, so the
// overwhelming majority render in full and the control never appears.
const COLLAPSE_THRESHOLD = 1200;

// Customers submitting in-app feedback attach screenshots, which arrive embedded in the
// ticket body as markdown. ~18% of tickets have them, and for those the words alone are
// often meaningless ("it looks like this"), so the image is the feedback.
const MARKDOWN_IMAGE = /!\[[^\]]*\]\((https?:\/\/[^\s)]+)\)/g;

// Video attachments arrive as a bare filename with no URL at all. The filename is
// prefixed with the epoch-seconds upload time, and the bucket partitions by that same
// month, so the real URL is reconstructable. Verified against live objects.
const BARE_VIDEO = /\b(\d{10})([A-Za-z0-9]+\.(?:mp4|mov))\b/gi;

function videoUrl(ts, rest) {
  const d = new Date(Number(ts) * 1000);
  const ym = `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  return `https://s3.coros.com/feedback/${ym}/${ts}${rest}`;
}

function splitBodyAndImages(raw) {
  if (!raw) return { text: '', images: [], videos: [] };
  const images = [...raw.matchAll(MARKDOWN_IMAGE)].map((m) => m[1]);
  const videos = [...raw.matchAll(BARE_VIDEO)].map((m) => videoUrl(m[1], m[2]));
  // Strip the markup and bare filenames so the prose reads cleanly, then collapse the
  // blank space they left behind.
  const text = raw
    .replace(MARKDOWN_IMAGE, '')
    .replace(BARE_VIDEO, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return { text, images, videos };
}

export function TicketCard({ ticket, showCluster = true }) {
  const [expanded, setExpanded] = useState(false);

  // Prefer the translated body, fall back to the original. description_english is
  // currently unpopulated for every ticket, so in practice this renders the original
  // language — still strictly better than the blank card this replaced, and it starts
  // showing English automatically if the translation backfill ever runs.
  const { text: body, images, videos } = splitBodyAndImages(
    ticket.description_english || ticket.description
  );
  const isLong = body && body.length > COLLAPSE_THRESHOLD;
  const shownBody = isLong && !expanded ? `${body.slice(0, COLLAPSE_THRESHOLD)}…` : body;

  const formattedDate = ticket.zendesk_created_at
    ? new Date(ticket.zendesk_created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : null;

  return (
    <div className="card">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            {/* Plain text, not a link — the body is readable inline, so the ticket
                number is an identifier. Zendesk is a deliberate action below. */}
            <span className="text-coros-text-muted font-mono text-sm">
              #{ticket.zendesk_id}
            </span>
            {ticket.is_representative && (
              <span className="badge bg-orange-500/20 text-orange-400">Representative</span>
            )}
          </div>
          {ticket.subject && (
            <h4 className="text-coros-text-primary font-medium">{ticket.subject}</h4>
          )}
        </div>
        {formattedDate && (
          <span className="text-coros-text-muted text-sm shrink-0 ml-4">{formattedDate}</span>
        )}
      </div>

      {/* Description — full body inline so it can be read without opening Zendesk */}
      {body && (
        <div className="mb-3">
          <p
            className="text-coros-text-secondary text-sm"
            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {shownBody}
          </p>
          {isLong && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-coros-red hover:text-coros-red-hover text-sm mt-1"
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              {expanded ? 'Show less' : 'Show full ticket'}
            </button>
          )}
        </div>
      )}

      {/* Attached screenshots — lazy so a 50-ticket page doesn't pull megabytes up front.
          Source images are full-resolution phone photos, hence the display cap. */}
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {images.map((src, i) => (
            <a key={src} href={src} target="_blank" rel="noopener noreferrer" title="Open full size">
              <img
                src={src}
                alt={`Attachment ${i + 1} from ticket ${ticket.zendesk_id}`}
                loading="lazy"
                style={{
                  height: 160, width: 'auto', maxWidth: 220, objectFit: 'cover',
                  borderRadius: 6, border: '1px solid var(--border-color)',
                  background: 'var(--bg-tertiary)', display: 'block',
                }}
              />
            </a>
          ))}
        </div>
      )}

      {/* Attached video. preload="none" so nothing downloads until it's played. */}
      {videos.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {videos.map((src) => (
            <video
              key={src}
              src={src}
              controls
              preload="none"
              style={{
                height: 160, maxWidth: 260, borderRadius: 6,
                border: '1px solid var(--border-color)', background: '#000',
              }}
            />
          ))}
        </div>
      )}

      {/* Metadata row */}
      <div className="flex items-center flex-wrap gap-x-4 gap-y-2 text-sm">
        {ticket.device_model && (
          <span className="text-coros-text-muted">
            <span className="text-coros-text-muted">Device:</span> {ticket.device_model}
          </span>
        )}
        {ticket.country && (
          <span className="text-coros-text-muted">
            <span className="text-coros-text-muted">Country:</span> {ticket.country}
          </span>
        )}
        {ticket.original_language && ticket.original_language !== 'en' && (
          <span className="badge bg-coros-bg-tertiary text-coros-text-secondary">
            {ticket.original_language.toUpperCase()}
          </span>
        )}
        {ticket.competitor_mentions && ticket.competitor_mentions.length > 0 && (
          <CompetitorBadges competitors={ticket.competitor_mentions} />
        )}
      </div>

      {/* Cluster link */}
      {showCluster && ticket.cluster && (
        <div className="mt-3 pt-3 border-t border-coros-border">
          <span className="text-coros-text-muted text-sm">Cluster: </span>
          <a
            href={`/clusters/${ticket.cluster.id}`}
            className="text-coros-red hover:text-coros-red-hover text-sm"
          >
            {ticket.cluster.name}
          </a>
        </div>
      )}
    </div>
  );
}

export default TicketCard;
